/**
 * Main careers orchestrator
 *
 * Fetches jobs from the configured source, renders the list, handles
 * detail views, filters, and browser history for deep links.
 *
 * @package Interact_26
 */

import { fetchPostings } from './api';
import { initFilters } from './filters';
import { renderJobDetail } from './render-job-detail';
import { renderJobsList, renderJobsLoading, renderJobsError } from './render-jobs-list';
import { type PinpointJob, type CareersConfig } from './types';
import {
  getMappedDepartment,
  matchesDepartmentFilter,
  normalizePosting,
  jobUrl,
  findJobBySlug,
  buildFilterDepartments,
} from './utils';

const JOB_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

let allJobs: PinpointJob[] = [];
let filteredJobs: PinpointJob[] = [];
let config: CareersConfig | null = null;

const getEl = (id: string): HTMLElement | null => document.getElementById(id);

/**
 * Show the appropriate register interest message based on job count.
 */
const updateRegisterMessage = (hasJobs: boolean): void => {
  const hasJobsEl = document.querySelector(
    '.careers-jobs__register--has-jobs',
  ) as HTMLElement | null;
  const noJobsEl = document.querySelector('.careers-jobs__register--no-jobs') as HTMLElement | null;

  if (hasJobsEl) hasJobsEl.hidden = !hasJobs;
  if (noJobsEl) noJobsEl.hidden = hasJobs;
};

/**
 * Hide all register interest messages.
 */
const hideRegisterMessages = (): void => {
  document.querySelectorAll<HTMLElement>('.careers-jobs__register').forEach((el) => {
    el.hidden = true;
  });
};

/**
 * Initialize the careers application.
 */
export const initCareers = async (): Promise<void> => {
  config = window.InteractCareersData ?? null;

  if (!config) {
    return;
  }

  const listContainer = getEl('careers-jobs-list');
  const detailContainer = getEl('careers-job-detail');
  const filtersContainer = getEl('careers-filter-chips');

  if (!listContainer || !detailContainer) {
    return;
  }

  // Show loading skeleton
  renderJobsLoading(listContainer);

  try {
    const rawPostings = await fetchPostings(config);
    allJobs = rawPostings.map(normalizePosting);
  } catch {
    renderJobsError(listContainer, config);
    return;
  }

  // Apply department filter if configured (department page)
  if (config.departmentFilter) {
    allJobs = allJobs.filter((job) => matchesDepartmentFilter(job, config!));
  }

  filteredJobs = [...allJobs];

  // Render initial job list
  renderJobsList(filteredJobs, listContainer, config, (jobId) => showJobDetail(jobId));

  // Show appropriate register interest message
  updateRegisterMessage(filteredJobs.length > 0);

  // Initialize filters (main page only)
  if (filtersContainer && !config.departmentFilter) {
    initFilters(
      filtersContainer,
      config,
      buildFilterDepartments(allJobs, config),
      (activeDepartments) => {
        if (activeDepartments.length === 0) {
          filteredJobs = [...allJobs];
        } else {
          filteredJobs = allJobs.filter((job) => {
            const mapped = getMappedDepartment(job.department, config!);
            return activeDepartments.includes(mapped);
          });
        }

        renderJobsList(filteredJobs, listContainer, config!, (jobId) => showJobDetail(jobId));
        updateRegisterMessage(filteredJobs.length > 0);
      },
    );
  }

  // Check for deep-linked job ID
  if (config.jobId && JOB_SLUG.test(config.jobId)) {
    showJobDetail(config.jobId);
  }

  // Handle browser back/forward
  window.addEventListener('popstate', (event) => {
    // Check pushState data first (most reliable)
    if (event.state?.jobId) {
      showJobDetail(String(event.state.jobId), false);
      return;
    }

    // Fallback: parse job ID from clean URL path (e.g. /careers/engineering/some-role)
    const match = window.location.pathname.match(/\/careers\/[^/]+\/([^/]+)\/?$/);
    if (match) {
      showJobDetail(match[1], false);
      return;
    }

    showJobsList(false);
  });
};

/**
 * Show the detail view for a specific job.
 */
const showJobDetail = (jobId: string, pushState = true): void => {
  if (!config) return;

  const detailContainer = getEl('careers-job-detail');
  const listContainer = getEl('careers-jobs-list');
  const filtersContainer = getEl('careers-filter-chips');
  const headingEl = document.querySelector('.careers-jobs__heading') as HTMLElement | null;
  const backLinkEl = document.querySelector('.careers-jobs__back-link') as HTMLElement | null;
  const benefitsEl = document.querySelector('.careers-benefits') as HTMLElement | null;

  if (!detailContainer || !listContainer) return;

  const job = findJobBySlug(allJobs, jobId);
  if (!job) return;

  renderJobDetail(job, detailContainer, config, () => showJobsList());

  // Toggle visibility — hide everything except the detail
  listContainer.hidden = true;
  detailContainer.hidden = false;

  if (filtersContainer) {
    filtersContainer.hidden = true;
  }

  hideRegisterMessages();

  if (headingEl) {
    headingEl.hidden = true;
  }

  if (backLinkEl) {
    backLinkEl.hidden = true;
  }

  // Department pages render this below the list; a job URL loaded fresh has no
  // such section, so hiding it keeps both routes to the same job identical.
  if (benefitsEl) {
    benefitsEl.hidden = true;
  }

  // Update URL
  const url = jobUrl(job, config);

  if (pushState) {
    window.history.pushState({ jobId: job.id }, job.title, url);
  } else if (job.id !== jobId) {
    // Arrived on a Pinpoint-era URL — swap it for the canonical one.
    window.history.replaceState({ jobId: job.id }, job.title, url);
  }

  // Scroll to top of section
  const section = detailContainer.closest('.careers-jobs');
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

/**
 * Show the jobs list and hide the detail view.
 */
const showJobsList = (pushState = true): void => {
  if (!config) return;

  const detailContainer = getEl('careers-job-detail');
  const listContainer = getEl('careers-jobs-list');
  const filtersContainer = getEl('careers-filter-chips');
  const headingEl = document.querySelector('.careers-jobs__heading') as HTMLElement | null;
  const backLinkEl = document.querySelector('.careers-jobs__back-link') as HTMLElement | null;
  const benefitsEl = document.querySelector('.careers-benefits') as HTMLElement | null;

  if (!detailContainer || !listContainer) return;

  // Toggle visibility — restore list view
  listContainer.hidden = false;
  detailContainer.hidden = true;
  detailContainer.innerHTML = '';

  if (filtersContainer) {
    filtersContainer.hidden = false;
  }

  updateRegisterMessage(filteredJobs.length > 0);

  if (headingEl) {
    headingEl.hidden = false;
  }

  if (backLinkEl) {
    backLinkEl.hidden = false;
  }

  if (benefitsEl) {
    benefitsEl.hidden = false;
  }

  // Update URL back to careers page
  if (pushState) {
    window.history.pushState(null, '', config.careersPageUrl);
  }
};
